import clsx from 'clsx';
// mantine
import { Box, Button, Divider, Skeleton, Text } from '@mantine/core';
import { useHotkeys, useOs } from '@mantine/hooks';
import { Link } from 'react-router-dom';
import { Command, Search } from 'lucide-react';
// components
import { Truncate } from '@newo/ui-kit';
import type { FC, ReactNode } from 'react';
import UserMenu from '@/components/Layout/components/UserMenu';
import PublishProjectButton from '@/modules/Projects/components/PublishProjectButton';
// utils
import { useProfileQuery } from '@/services/customer/profile/profile.queries.ts';
import useSearchStore from '@/components/Layout/components/Search/Search.store';
// assets
import styles from '@/components/Layout/components/Header/Header.module.css';
import BillingNotificationBanner from '@/components/BillingNotificationBanner';

interface HeaderProps {
    className?: string;
    children?: ReactNode;
    breadcrumbs: {
        label: ReactNode;
        href?: string;
    }[];
    isSearchVisible?: boolean;
    isPublishAllVisible?: boolean;
}

const Header: FC<HeaderProps> = ({
    className,
    children,
    breadcrumbs,
    isSearchVisible = false,
    isPublishAllVisible = true
}) => {
    const { data: profile, isSuccess: isProfileSuccess } = useProfileQuery();
    const { toggle } = useSearchStore();
    const os = useOs();

    useHotkeys([
        [
            'mod+k',
            () => {
                if (isSearchVisible) {
                    toggle();
                }
            }
        ]
    ]);

    return (
        <div className={styles.wrapper}>
            <BillingNotificationBanner className={styles.alert} />
            <header className={clsx(styles.header, className)}>
                <div className="flex items-start gap-2">
                    {isProfileSuccess ? (
                        <div className="leading-none">
                            <Truncate
                                className="font-bold"
                                endLength={12}
                                mode="middle"
                                startLength={12}
                                text={profile.organization_name ?? ''}
                            />
                            <div className="text-xs/5 text-gray-6">
                                Idn: {profile.idn}
                            </div>
                        </div>
                    ) : (
                        <Skeleton height={10} mb={2} width={200} />
                    )}
                    {breadcrumbs.length > 0 &&
                        breadcrumbs.map((breadcrumb) => {
                            let content = breadcrumb.label;
                            const key =
                                typeof breadcrumb.label === 'string'
                                    ? breadcrumb.label
                                    : breadcrumb.href;

                            if (
                                typeof breadcrumb.label === 'string' &&
                                !breadcrumb.href
                            ) {
                                content = (
                                    <span className="leading-none whitespace-nowrap">
                                        {breadcrumb.label}
                                    </span>
                                );
                            }
                            if (
                                breadcrumb.href &&
                                typeof breadcrumb.label === 'string'
                            ) {
                                content = (
                                    <Link
                                        className="text-black whitespace-nowrap leading-none"
                                        to={breadcrumb.href}
                                    >
                                        {breadcrumb.label}
                                    </Link>
                                );
                            }
                            return (
                                <span className="contents" key={key}>
                                    <span className="text-gray-6 leading-none">
                                        /
                                    </span>
                                    {content}
                                </span>
                            );
                        })}
                </div>
                <div className="ml-auto flex items-center gap-2">
                    {children}
                </div>
                {isPublishAllVisible ? (
                    <>
                        <PublishProjectButton>Publish All</PublishProjectButton>
                        <Divider orientation="vertical" size="sm" />
                    </>
                ) : undefined}
                {isSearchVisible ? (
                    <>
                        <Button
                            leftSection={<Search />}
                            size="xs"
                            variant="default"
                            onClick={toggle}
                        >
                            <Box
                                bg="gray.1"
                                className="flex items-center justify-center rounded gap-0.5"
                                px="3px"
                                py="1px"
                            >
                                {os === 'macos' ? (
                                    <Command color="#99A1AF" />
                                ) : (
                                    <Text c="#99A1AF" size="xs">
                                        CTRL+
                                    </Text>
                                )}
                                <Text c="#99A1AF" size="xs">
                                    K
                                </Text>
                            </Box>
                        </Button>
                        <Divider orientation="vertical" size="sm" />
                    </>
                ) : undefined}

                <UserMenu profile={profile} />
            </header>
        </div>
    );
};

export default Header;
